import { z } from "zod";

// ── Enums ──

export enum CopywritingChannel {
	LINKEDIN = "linkedin",
	EMAIL = "email",
	BOTH = "both",
}

export enum CopywritingOutputFormat {
	FULL_SEQUENCE = "full_sequence",
	AI_SNIPPETS = "ai_snippets",
}

export enum CopywritingStage {
	DEEP_RESEARCH = "DEEP_RESEARCH",
	ASSEMBLING_CONTEXT = "ASSEMBLING_CONTEXT",
	RESEARCHING_LEAD = "RESEARCHING_LEAD",
	SELECTING_STRATEGY = "SELECTING_STRATEGY",
	LOADING_TONE = "LOADING_TONE",
	WRITING_MESSAGES = "WRITING_MESSAGES",
	FINALIZING = "FINALIZING",
}

export enum CopywritingStatus {
	IDLE = "IDLE",
	QUEUED = "QUEUED",
	RUNNING = "RUNNING",
	COMPLETED = "COMPLETED",
	FAILED = "FAILED",
}

export const STAGE_LABELS: Record<CopywritingStage, string> = {
	[CopywritingStage.DEEP_RESEARCH]: "Running deep research...",
	[CopywritingStage.ASSEMBLING_CONTEXT]: "Assembling context...",
	[CopywritingStage.RESEARCHING_LEAD]: "Researching lead...",
	[CopywritingStage.SELECTING_STRATEGY]: "Selecting strategy...",
	[CopywritingStage.LOADING_TONE]: "Loading tone of voice...",
	[CopywritingStage.WRITING_MESSAGES]: "Writing messages...",
	[CopywritingStage.FINALIZING]: "Finalizing...",
};

export const STAGE_ORDER: CopywritingStage[] = [
	CopywritingStage.DEEP_RESEARCH,
	CopywritingStage.ASSEMBLING_CONTEXT,
	CopywritingStage.RESEARCHING_LEAD,
	CopywritingStage.SELECTING_STRATEGY,
	CopywritingStage.LOADING_TONE,
	CopywritingStage.WRITING_MESSAGES,
	CopywritingStage.FINALIZING,
];

export const LANGUAGE_NAME_MAP: Record<string, string> = {
	en: "English",
	de: "German",
	fr: "French",
	es: "Spanish",
	it: "Italian",
	nl: "Dutch",
	pt: "Portuguese",
	pl: "Polish",
	sv: "Swedish",
	da: "Danish",
	no: "Norwegian",
	fi: "Finnish",
	cs: "Czech",
	hu: "Hungarian",
	ro: "Romanian",
	bg: "Bulgarian",
	el: "Greek",
	tr: "Turkish",
	ar: "Arabic",
	ja: "Japanese",
	zh: "Chinese",
	ko: "Korean",
};

export const LANGUAGE_FLAG_MAP: Record<string, string> = {
	en: "🇬🇧",
	de: "🇩🇪",
	fr: "🇫🇷",
	es: "🇪🇸",
	it: "🇮🇹",
	nl: "🇳🇱",
	pt: "🇵🇹",
	pl: "🇵🇱",
	sv: "🇸🇪",
	da: "🇩🇰",
	no: "🇳🇴",
	fi: "🇫🇮",
	cs: "🇨🇿",
	hu: "🇭🇺",
	ro: "🇷🇴",
	bg: "🇧🇬",
	el: "🇬🇷",
	tr: "🇹🇷",
	ar: "🇸🇦",
	ja: "🇯🇵",
	zh: "🇨🇳",
	ko: "🇰🇷",
};

// ISO 639-1 language code → ISO 3166-1 alpha-2 country code (used for solid SVG flag rendering).
export const LANGUAGE_COUNTRY_MAP: Record<string, string> = {
	en: "GB",
	de: "DE",
	fr: "FR",
	es: "ES",
	it: "IT",
	nl: "NL",
	pt: "PT",
	pl: "PL",
	sv: "SE",
	da: "DK",
	no: "NO",
	fi: "FI",
	cs: "CZ",
	hu: "HU",
	ro: "RO",
	bg: "BG",
	el: "GR",
	tr: "TR",
	ar: "SA",
	ja: "JP",
	zh: "CN",
	ko: "KR",
};

const LANGUAGE_NAME_TO_ISO2: Record<string, string> = {
	english: "en",
	german: "de",
	deutsch: "de",
	french: "fr",
	français: "fr",
	francais: "fr",
	spanish: "es",
	español: "es",
	espanol: "es",
	italian: "it",
	italiano: "it",
	dutch: "nl",
	nederlands: "nl",
	portuguese: "pt",
	português: "pt",
	portugues: "pt",
	polish: "pl",
	polski: "pl",
	swedish: "sv",
	svenska: "sv",
	danish: "da",
	dansk: "da",
	norwegian: "no",
	norsk: "no",
	finnish: "fi",
	suomi: "fi",
	czech: "cs",
	čeština: "cs",
	cestina: "cs",
	hungarian: "hu",
	magyar: "hu",
	romanian: "ro",
	română: "ro",
	romana: "ro",
	bulgarian: "bg",
	български: "bg",
	greek: "el",
	ελληνικά: "el",
	turkish: "tr",
	türkçe: "tr",
	turkce: "tr",
	arabic: "ar",
	العربية: "ar",
	japanese: "ja",
	日本語: "ja",
	chinese: "zh",
	中文: "zh",
	korean: "ko",
	한국어: "ko",
};

/**
 * Normalize a language string (ISO code, full name, or native name) to ISO 639-1.
 * Handles comma-separated lists by taking the first entry.
 * Falls back to "en" if unrecognized.
 */
export const normalizeLanguageToISO2 = (input?: string | null): string => {
	if (!input) {
		return "en";
	}

	const raw = input.split(",")[0].trim().toLowerCase();

	// Already a 2-char ISO code
	if (raw.length === 2 && LANGUAGE_FLAG_MAP[raw]) {
		return raw;
	}

	// Lookup by full name
	if (LANGUAGE_NAME_TO_ISO2[raw]) {
		return LANGUAGE_NAME_TO_ISO2[raw];
	}

	// If it's a 2-char string not in our flag map, still return it
	if (raw.length === 2) {
		return raw;
	}

	return "en";
};

// ── Request Schema ──

export const FrameworkOverridesSchema = z.object({
	step1FrameworkId: z.number().nullable().optional(),
	step2FrameworkId: z.number().nullable().optional(),
	step3FrameworkId: z.number().nullable().optional(),
});

export type FrameworkOverrides = z.infer<typeof FrameworkOverridesSchema>;

export const GenerateCopywritingRequestSchema = z.object({
	prospectId: z.number(),
	listId: z.number(),
	channel: z.nativeEnum(CopywritingChannel),
	language: z.string(),
	senderUserId: z.number(),
	senderEmailId: z.number().optional(),
	userInstruction: z.string().optional(),
	linkedinInviteNote: z.boolean(),
	outputFormat: z.nativeEnum(CopywritingOutputFormat),
	frameworkOverrides: FrameworkOverridesSchema.optional(),
	templates: z.record(z.string(), z.string()).optional(),
	freshStartOverride: z.boolean().optional(),
	// Tracker signal the generation is seeded from (feed per-signal CTA, PREC-3635 P6).
	// Surfaced back via GET /active + the company-room socket to match rows to jobs.
	seedSignalId: z.number().optional(),
	// Generate without any signal trigger — set when the user removes the signal chip (PREC-3710).
	ignoreSignal: z.boolean().optional(),
});

export type GenerateCopywritingRequest = z.infer<typeof GenerateCopywritingRequestSchema>;

// ── Batch Request Schema ──

export const GenerateBatchCopywritingRequestSchema = z.object({
	listId: z.number(),
	// Explicit ids for a CURRENT_PAGE selection. Omitted for an ALL selection, which the backend
	// resolves from listId + the filter/status fields below (bulk "select all across pages").
	listPersonIds: z.array(z.number()).min(1).optional(),
	channel: z.nativeEnum(CopywritingChannel),
	senderUserIds: z.array(z.number()).min(1),
	senderEmailId: z.number().optional(),
	language: z.string().optional(),
	userInstruction: z.string().optional(),
	linkedinInviteNote: z.boolean(),
	outputFormat: z.nativeEnum(CopywritingOutputFormat),
	frameworkOverrides: FrameworkOverridesSchema.optional(),
	templates: z.record(z.string(), z.string()).optional(),
	// Bulk "select all across pages" — mirrors the other list bulk endpoints. When selectType is
	// ALL the server resolves every matching lead from listId + these filters.
	selectType: z.string().optional(),
	filter: z.record(z.string(), z.any()).optional(),
	inclusionFilterStatus: z.array(z.string()).optional(),
	priorityFilterStatus: z.array(z.string()).optional(),
	signalFilterStatus: z.array(z.string()).optional(),
	assignStatus: z.string().optional(),
	// The table's search: a select-all made while searching covers the matches only (PREC-4443).
	searchValue: z.string().optional(),
	// Every status of the tab the table showed, and its reason sub-filters (PREC-4450).
	assignStatuses: z.array(z.string()).optional(),
	exclusionReasons: z.array(z.string()).optional(),
});

export type GenerateBatchCopywritingRequest = z.infer<typeof GenerateBatchCopywritingRequestSchema>;

// ── Response Schemas ──

export const FullSequenceResponseSchema = z.object({
	prospectId: z.number(),
	listPersonId: z.number(),
	eventId: z.number(),
	type: z.literal("full_sequence"),
	contextPayload: z.any(),
	messagingStrategy: z.any(),
	sequenceOutput: z.any(),
	validation: z.any(),
});

export const AiSnippetsResponseSchema = z.object({
	prospectId: z.number(),
	listPersonId: z.number(),
	eventId: z.number(),
	type: z.literal("ai_snippets"),
	contextPayload: z.any(),
	snippetOutput: z.any(),
});

export const GenerateCopywritingResponseSchema = z.discriminatedUnion("type", [
	FullSequenceResponseSchema,
	AiSnippetsResponseSchema,
]);

export type GenerateCopywritingResponse = z.infer<typeof GenerateCopywritingResponseSchema>;

// ── REST Ack Schema (202 Accepted) ──

export const GenerateCopywritingAckSchema = z.object({
	listPersonId: z.number(),
	prospectId: z.number(),
	listId: z.number(),
	status: z.literal(CopywritingStatus.QUEUED),
	eventId: z.number(),
});

export type GenerateCopywritingAck = z.infer<typeof GenerateCopywritingAckSchema>;

// ── Batch Response Schema ──

export const BatchAcceptedItemSchema = z.object({
	listPersonId: z.number(),
	prospectId: z.number(),
	eventId: z.number(),
	language: z.string(),
	senderUserId: z.number(),
});

export const BatchSkippedItemSchema = z.object({
	listPersonId: z.number(),
	reason: z.enum(["not_found", "list_mismatch", "already_in_progress"]),
});

export const GenerateBatchCopywritingResponseSchema = z.object({
	accepted: z.array(BatchAcceptedItemSchema),
	skipped: z.array(BatchSkippedItemSchema),
});

export type GenerateBatchCopywritingResponse = z.infer<
	typeof GenerateBatchCopywritingResponseSchema
>;

// ── WebSocket event types ──

export interface CopywritingStageUpdate {
	stage: CopywritingStage;
	label: string;
	prospectId: number;
}

export interface CopywritingGenerationQueuedEvent {
	prospectId: number;
	listPersonId: number;
	eventId: number;
}

export interface CopywritingGenerationError {
	prospectId: number;
	listPersonId: number;
	eventId: number;
	error: string;
}

export const CopywritingGenerationErrorSchema = z.object({
	prospectId: z.number(),
	listPersonId: z.number(),
	eventId: z.number(),
	error: z.string(),
});

// ── Socket event names ──

export enum CopywritingSocketEvent {
	JOIN_GENERATION = "copywriting:join_generation",
	GENERATION_QUEUED = "copywriting:generation_queued",
	STAGE_UPDATE = "copywriting:stage_update",
	GENERATION_COMPLETE = "copywriting:generation_complete",
	GENERATION_ERROR = "copywriting:generation_error",
}

// ── Tone of voice (mirrors backend ToneOfVoiceMessageType / ToneOfVoiceChannel) ──

export enum ToneOfVoiceMessageType {
	LI_CONNECTION_REQUEST = "LI_CONNECTION_REQUEST",
	LI_INTRODUCTION = "LI_INTRODUCTION",
	LI_FOLLOW_UP = "LI_FOLLOW_UP",
	LI_BREAKUP = "LI_BREAKUP",
	EMAIL_INTRODUCTION = "EMAIL_INTRODUCTION",
	EMAIL_FOLLOW_UP = "EMAIL_FOLLOW_UP",
	EMAIL_BREAKUP = "EMAIL_BREAKUP",
	REPLY_INITIAL = "REPLY_INITIAL",
	REPLY_FOLLOW_UP = "REPLY_FOLLOW_UP",
	REPLY_NURTURING = "REPLY_NURTURING",
}

export enum ToneOfVoiceChannel {
	LINKEDIN = "LINKEDIN",
	EMAIL = "EMAIL",
}

export interface ToneOfVoiceLeadContext {
	personaCategory?: string;
	companyCategory?: string;
	triggerType?: string;
}

export interface ToneOfVoiceCapturePayload {
	originalText: string;
	messageType: ToneOfVoiceMessageType;
	channel: ToneOfVoiceChannel;
	language?: string;
	frameworkUsed?: string;
	leadContext?: ToneOfVoiceLeadContext;
}

// ── Draft item produced from an AI copywriting result ──

export interface AiDraftItem {
	content: string;
	subject?: string;
	channel: "email" | "linkedin";
	scheduledInDays?: number;
	isInviteNote?: boolean;
	aiOrigin?: ToneOfVoiceCapturePayload;
}
